import React, { useEffect, useState } from 'react';

export default function EcomGeneratorModal({ open, onClose, initialTemplateId }: { open: boolean, onClose: () => void, initialTemplateId?: string | null }) {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [vars, setVars] = useState<Record<string, any>>({});
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskRes, setTaskRes] = useState<any>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch('/api/ecom/templates');
        const j = await r.json();
        if (j && j.data) setTemplates(j.data || []);
      } catch (e) {
        console.error('fetch templates failed', e);
      } finally { setLoading(false); }
    })();
  }, [open]);

  // when templates load or initialTemplateId changes, pre-select if provided
  useEffect(() => {
    if (!open) return;
    if (!templates || templates.length === 0) return;
    if (initialTemplateId) {
      const tpl = templates.find(t => t.id === initialTemplateId);
      if (tpl) setSelected(tpl);
    } else if (templates.length === 1) {
      // if only one template configured, auto-select it for smoother UX
      setSelected(templates[0]);
    }
  }, [templates, initialTemplateId, open]);

  useEffect(() => {
    if (selected) {
      const defs = selected.defaults || {};
      setVars(Object.assign({}, defs));
    } else {
      setVars({});
    }
  }, [selected]);

  function resolveTemplateString(tpl: string = '', varsObj: Record<string, any> = {}) {
    if (!tpl) return '';
    return String(tpl).replace(/{{\s*([^}]+)\s*}}/g, (m, key) => {
      const v = varsObj[key.trim()];
      return v === undefined || v === null ? '' : String(v);
    });
  }

  useEffect(() => {
    let timer: any = null;
    if (polling && taskId) {
      timer = setInterval(async () => {
        try {
          const r = await fetch(`/api/ecom/tasks/${taskId}`);
          const j = await r.json();
          if (j && j.data) setTaskRes(j.data);
          if (j && j.data && (j.data.task.status === 'completed' || j.data.task.status === 'failed' || j.data.task.status === 'partial_failed')) {
            setPolling(false);
            clearInterval(timer);
          }
        } catch (e) {
          console.error('poll task failed', e);
        }
      }, 3000);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [polling, taskId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg max-w-2xl w-full p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">一键生成电商图片</h3>
          <button onClick={onClose} className="text-sm text-gray-600">关闭</button>
        </div>

        <div className="mt-3">
          {loading && <div>加载模板...</div>}
          {!loading && (
            <>
              <label className="block text-sm">模板</label>
              <select className="w-full border p-2 rounded" value={selected?.id || ''} onChange={(e) => {
                const tpl = templates.find(t => t.id === e.target.value);
                setSelected(tpl || null);
              }}>
                <option value="">请选择模板</option>
                {templates.map(t => <option value={t.id} key={t.id}>{t.name}</option>)}
              </select>

              {selected && (
                <div className="mt-3">
                  {/* 模板预览区 */}
                  <div className="mb-3">
                    <div className="text-sm font-medium mb-2">模板预览</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {(Array.isArray(selected.outputs) ? selected.outputs : []).map((out: any) => {
                        const promptTpl = out.prompt || out.promptTemplate || out.template || out.prompt_template || out.prompt_text || out.prompt_text || '';
                        const resolved = resolveTemplateString(promptTpl, Object.assign({}, selected.defaults || {}, vars || {}));
                        const sizeLabel = out.size || (out.width && out.height ? `${out.width}x${out.height}` : '—');
                        return (
                          <div key={out.key || out.label || Math.random()} className="p-3 border rounded-lg bg-gray-50">
                            <div className="h-36 w-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">示例图片 • {out.label || out.key}</div>
                            <div className="mt-2 text-sm font-medium">{out.label || out.key}</div>
                            <div className="text-xs text-slate-500">大小: {sizeLabel} · 模型: {out.model || '默认'}</div>
                            {out.price ? <div className="text-xs text-amber-700 mt-1">价格: {out.price} CNY</div> : null}
                            <details className="mt-2 text-xs">
                              <summary className="cursor-pointer text-slate-600">查看示例提示词</summary>
                              <div className="whitespace-pre-wrap text-xs text-slate-700 mt-1">{resolved || (promptTpl ? promptTpl : '无')}</div>
                            </details>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">填写变量（可选）</div>
                  {Object.keys(selected.defaults || {}).length === 0 && <div className="text-xs text-gray-400">该模板无默认变量，可直接提交。</div>}
                  {Object.entries(selected.defaults || {}).map(([k, v]) => (
                    <div key={k} className="mt-2">
                      <label className="block text-xs">{k}</label>
                      <input className="w-full border p-2 rounded" value={vars[k] ?? ''} onChange={(e) => setVars(prev => ({ ...prev, [k]: e.target.value }))} />
                    </div>
                  ))}

                  <div className="mt-4 flex items-center gap-2">
                    <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={async () => {
                      // submit
                      try {
                        setLoading(true);
                        const r = await fetch('/api/ecom/generate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ templateId: selected.id, variables: vars }) });
                        const j = await r.json();
                        if (j && j.data && j.data.taskId) {
                          setTaskId(j.data.taskId);
                          setPolling(true);
                        } else {
                          alert('创建任务失败');
                        }
                      } catch (e) { console.error(e); alert('请求失败'); }
                      finally { setLoading(false); }
                    }}>提交生成</button>

                    <button className="px-3 py-2 border rounded" onClick={() => { setSelected(null); setVars({}); setTaskId(null); setTaskRes(null); }}>重置</button>
                  </div>

                  {taskRes && (
                    <div className="mt-4">
                      <div className="font-medium">任务状态：{taskRes.task.status}</div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {(taskRes.images || []).map(img => (
                          <div key={img.id} className="border p-2">
                            {img.image_url ? (
                              <>
                                <img src={img.image_url} alt={img.output_key} className="w-full h-40 object-cover" />
                                <div className="mt-2 flex gap-2">
                                  <a className="px-3 py-1 bg-green-600 text-white rounded text-xs" href={img.image_url} target="_blank" rel="noreferrer">查看</a>
                                  <a className="px-3 py-1 border rounded text-xs" href={img.image_url} download>下载</a>
                                  <button className="px-3 py-1 border rounded text-xs" onClick={async () => {
                                    try {
                                      setLoading(true);
                                      const r = await fetch('/api/ecom/regenerate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskId: taskRes.task.id, outputKey: img.output_key }) });
                                      const j = await r.json();
                                      if (r.ok && j && j.data) {
                                        // start polling for the task again
                                        setTaskId(taskRes.task.id);
                                        setPolling(true);
                                        alert('已消耗一次调整次数，正在生成');
                                      } else {
                                        alert(j.msg || '请求失败');
                                      }
                                    } catch (e) { console.error(e); alert('请求失败'); }
                                    finally { setLoading(false); }
                                  }}>调整/再生</button>
                                </div>
                              </>
                            ) : (
                              <div className="text-sm text-gray-500">未生成</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
